import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import introducedInvestorService, { Step4CompletionDto } from '../../../services/introducedInvestorService';
import TermsModal from './TermsModal';
import './IntroducedInvestorRegistration.scss';
import { PremiumSelect } from '../../../components/PremiumSelect/PremiumSelect';
import { CompactHeader } from '../../../components/CompactHeader/CompactHeader';
import { CompactFooter } from '../../../components/CompactFooter/CompactFooter';
import { RegistrationStepper } from '../../../components/RegistrationStepper/RegistrationStepper';
import { RegistrationVisualCard } from '../../../components/RegistrationVisualCard/RegistrationVisualCard';

/**
 * Step 4: Final Registration Details (Nationality-specific fields)
 * Matches Laravel: introduce-register-step4.blade.php
 */
const IntroducedInvestorStep4: React.FC = () => {
  const { uniqueCode } = useParams<{ uniqueCode: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showLegalEntityDiv, setShowLegalEntityDiv] = useState(false);
  const [showWhatsappDiv, setShowWhatsappDiv] = useState(false);
  const [showDiffWhatsappDiv, setShowDiffWhatsappDiv] = useState(false);
  const [sameWhatsapp, setSameWhatsapp] = useState<string | null>(null);

  // Modal states
  // Privacy consent is captured earlier in the flow (consent step), so step4 only gates on Terms — matches Laravel.
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsCheckboxEnabled, setTermsCheckboxEnabled] = useState(false);

  const [formData, setFormData] = useState<Step4CompletionDto>({
    uniqueCode: uniqueCode!,
    selfOrLegalEntity: 'Self',
    nationality: '',
    countryOfResidence: '',
    // Blank, NOT 'Yes'. These are answered at Step 1 and only mirrored here so the completion
    // payload is unchanged. Defaulting them to 'Yes' would resurrect the bug we just fixed: a
    // question that did not apply (an Indian national never sees "person of Indian origin")
    // comes back null from prefill, and `?? prev` below would then submit a fabricated 'Yes'
    // that outranks the session's real null in the server's firstNonBlankValue fallback.
    panCardStatus: '',
    indianOrigin: '',
    ociCardStatus: '',
    entityName: '',
    legalPanCard: 'Yes',
    representativeCapacity: 'Director',
    securityRegulated: 'Yes',
    termsAccepted: false,
    privacyPolicyAccepted: false,
    agreeForWhatsapp: false,
    agreeForMarketing: false,
    whatsappNumber: '',
    whatsappCountryCode: '+91'
  });

  // Prefill nationality/country and the Step 1 identity answers from the session.
  // Mirrors Laravel introduce-register-step4: `nationality` is the hidden intro_dv_nationality GUID
  // (not a user choice), and JS toggles the India (PAN) vs non-India (OCI) section from it. Here the
  // India decision is resolved server-side by name (nationalityIsIndia) — no hardcoded nationality GUID.
  // No country-of-residence master fetch: that dropdown lives on Step 1 now.
  useEffect(() => {
    if (!uniqueCode) return;
    introducedInvestorService.getSessionPrefill(uniqueCode)
      .then(res => {
        const d = res.data;
        setFormData(prev => ({
          ...prev,
          nationality: d.nationalityGuid ?? prev.nationality,
          // Collected at Step 1 now; still read here so the completion payload is unchanged
          // and investor-type derivation keeps the same inputs.
          countryOfResidence: d.countryOfResidenceGuid ?? prev.countryOfResidence,
          ociCardStatus: d.ociCardStatus ?? prev.ociCardStatus,
          panCardStatus: d.panCardStatus ?? prev.panCardStatus,
          indianOrigin: d.indianOrigin ?? prev.indianOrigin,
        }));
      })
      .catch(err => console.error('Error fetching step4 prefill:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniqueCode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Nationality no longer toggles anything on this step: the India/non-India questions
    // (PAN, OCI, Person of Indian Origin) are all asked at Step 1. It is still submitted.

    // Handle WhatsApp agreement
    if (name === 'agreeForWhatsapp') {
      setShowWhatsappDiv(checked);
    }

    // Handle self/legal entity change
    if (name === 'selfOrLegalEntity') {
      setShowLegalEntityDiv(value === 'Legal Entity');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!termsAccepted) {
      alert('Please read and accept the Terms and Conditions to continue');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const submitData: Step4CompletionDto = {
        ...formData,
        termsAccepted: termsAccepted,
        // Privacy was already accepted at the consent step earlier in the flow.
        privacyPolicyAccepted: true
      };

      const response = await introducedInvestorService.completeRegistration(submitData);

      if (response.data.success) {
        // Any assigned Service Agent is parked as a PENDING delegation server-side and the
        // investor consents later in the Permissions Center, so registration ends at success.
        navigate('/investor/introduced/success', {
          state: {
            message: response.data.message,
            b2cCreated: response.data.b2cAccountCreated,
            // Lets the success screen offer "Set Your Password" directly instead of
            // sending the investor off to find the email first.
            azureUserId: response.data.azureUserId
          }
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to complete registration');
      console.error('Error completing registration:', err);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  const openTermsModal = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    setShowTermsModal(true);
  };

  const closeTermsModal = () => {
    setShowTermsModal(false);
  };

  const acceptTerms = () => {
    setTermsCheckboxEnabled(true);
    setTermsAccepted(true);
    setFormData(prev => ({ ...prev, termsAccepted: true }));
    setShowTermsModal(false);
  };

  return (
    <>
      <CompactHeader />
      <section className="login-form-style4 steps4-sec section-padding align-items-center">
        <div className="container">
          <div className="registration-split-layout">
            {/* Left Panel: Form & Stepper */}
            <div className="left-panel">
              {/* Stepper Progress */}
              <RegistrationStepper currentStep={5} title="Registration Details" maxWidth="850px" />

              {/* Investor Registration Card */}
              <div className="login-form-style3-main" style={{ width: '100%', maxWidth: '850px', margin: '0 auto' }}>
                <div className="login-form-style3-main_full">
                  {error && <div className="alert alert-danger">{error}</div>}

                  <div className="login-register3-form-middle" style={{ textAlign: 'left' }}>
                    <form onSubmit={handleSubmit} id="investor_form">
                      <input type="hidden" name="unique_code" value={uniqueCode} />
                      <input type="hidden" name="market_invest" value="Self" />

                      {/* Self or Legal Entity Selection */}
                      {formData.selfOrLegalEntity === 'Self' && (
                        <div className="name-sec" id="section_self_div">
                          {/* Both nationality branches are now empty: PAN, OCI, Person of
                              Indian Origin and Country of Residency are all asked at Step 1,
                              where the identity questions belong together. Their values still
                              travel in this step's payload, read back from the session prefill.
                          */}
                        </div>
                      )}

                      {/* Legal Entity Fields */}
                      {showLegalEntityDiv && (
                        <div className="name-sec" id="section_legal_div">
                          <div id="legal_country_div">
                            <div className="single-field self-sec1">
                              <label htmlFor="legalPanCard">
                                <i className="fa-solid fa-building"></i> Do you have pan card? <span className="star-color">*</span>
                                <i className="fa-solid fa-circle-info" title="Permanent Account Number (PAN) is a ten-digit tax identification alphanumeric number, issued by Income-tax Department, Government of India" style={{ marginLeft: '4px', color: '#2c5966', cursor: 'help' }}></i>
                              </label>
                              <div className="radio-box">
                                <label className="radio">
                                  <input
                                    name="legalPanCard"
                                    type="radio"
                                    value="Yes"
                                    checked={formData.legalPanCard === 'Yes'}
                                    onChange={handleChange}
                                  />
                                  <span>Yes</span>
                                </label>
                                <label className="radio">
                                  <input
                                    name="legalPanCard"
                                    type="radio"
                                    value="No"
                                    checked={formData.legalPanCard === 'No'}
                                    onChange={handleChange}
                                  />
                                  <span>No</span>
                                </label>
                              </div>
                            </div>

                            <div className="single-field self-sec1">
                              <label htmlFor="representativeCapacity">
                                <i className="fa-solid fa-building"></i> In what capacity representing company: <span className="star-color">*</span>
                              </label>
                              <div className="radio-box">
                                <label className="radio">
                                  <input
                                    name="representativeCapacity"
                                    type="radio"
                                    value="Director"
                                    checked={formData.representativeCapacity === 'Director'}
                                    onChange={handleChange}
                                  />
                                  <span>Director</span>
                                </label>
                                <label className="radio">
                                  <input
                                    name="representativeCapacity"
                                    type="radio"
                                    value="Employee"
                                    checked={formData.representativeCapacity === 'Employee'}
                                    onChange={handleChange}
                                  />
                                  <span>Employee</span>
                                </label>
                                <label className="radio">
                                  <input
                                    name="representativeCapacity"
                                    type="radio"
                                    value="POA Holder"
                                    checked={formData.representativeCapacity === 'POA Holder'}
                                    onChange={handleChange}
                                  />
                                  <span>POA Holder</span>
                                </label>
                              </div>
                            </div>

                            <div className="single-field self-sec1">
                              <label htmlFor="securityRegulated">
                                <i className="fa-solid fa-lock"></i> Whether Company is regulated as Securities or Banking Company: <span className="star-color">*</span>
                              </label>
                              <div className="radio-box">
                                <label className="radio">
                                  <input
                                    name="securityRegulated"
                                    type="radio"
                                    value="Yes"
                                    checked={formData.securityRegulated === 'Yes'}
                                    onChange={handleChange}
                                  />
                                  <span>Yes</span>
                                </label>
                                <label className="radio">
                                  <input
                                    name="securityRegulated"
                                    type="radio"
                                    value="No"
                                    checked={formData.securityRegulated === 'No'}
                                    onChange={handleChange}
                                  />
                                  <span>
                                    No
                                    <i className="fa-solid fa-circle-info" title="Regulated Entity: Is the entity regulated as a securities or banking provider?" style={{ marginLeft: '4px', color: '#2c5966', cursor: 'help' }}></i>
                                  </span>
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* WhatsApp Communication */}
                      <div id="note_confirm_div" className="consent-container m-0 p-0" style={{ marginTop: '24px', background: 'transparent', boxShadow: 'none' }}>
                        <div
                          className="highlight"
                          style={{
                            backgroundColor: formData.agreeForWhatsapp ? '#dcf1f6' : '#fafbfc',
                            borderLeftColor: formData.agreeForWhatsapp ? '#2c5966' : '#cbd5e1',
                            borderLeftStyle: 'solid',
                            borderLeftWidth: '4px'
                          }}
                        >
                          <div className="checkbox-wrapper-33">
                            <label className="checkbox">
                              <input
                                className="checkbox__trigger visuallyhidden"
                                type="checkbox"
                                name="agreeForWhatsapp"
                                checked={formData.agreeForWhatsapp}
                                onChange={handleChange}
                              />
                              <span className="checkbox__symbol">
                                <svg aria-hidden="true" className="icon-checkbox" width="28px" height="28px" viewBox="0 0 28 28" version="1" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M4 14l8 7L24 7"></path>
                                </svg>
                              </span>
                              <p className="checkbox__textwrapper" style={{ fontWeight: '600' }}>
                                I agree to receive communication on WhatsApp
                              </p>
                            </label>
                          </div>

                          {showWhatsappDiv && (
                            <div id="whatsapp_no_div" className="mt-3">
                              <div className="single-field">
                                <label htmlFor="sameWhatsapp">
                                  Is your mobile number the same as your WhatsApp number?
                                  <span className="star-color">*</span>
                                </label>

                                <div className="d-flex flex-row gap-3 mt-2" style={{ width: '100%', maxWidth: '480px' }}>
                                  <div
                                    className={`market-choice-card m-0 p-2 ${sameWhatsapp === 'Yes' ? 'selected' : ''}`}
                                    onClick={() => {
                                      setSameWhatsapp('Yes');
                                      setShowDiffWhatsappDiv(false);
                                    }}
                                  >
                                    <div className="square-indicator">
                                      <i className="bi bi-check-lg" />
                                    </div>
                                    <span className="choice-label">Yes</span>
                                  </div>

                                  <div
                                    className={`market-choice-card m-0 p-2 ${sameWhatsapp === 'No' ? 'selected' : ''}`}
                                    onClick={() => {
                                      setSameWhatsapp('No');
                                      setShowDiffWhatsappDiv(true);
                                    }}
                                  >
                                    <div className="square-indicator">
                                      <i className="bi bi-check-lg" />
                                    </div>
                                    <span className="choice-label">No</span>
                                  </div>
                                </div>
                              </div>

                              {showDiffWhatsappDiv && (
                                <div className="single-field mt-3" id="second_mob_div">
                                  <label htmlFor="whatsappNumber">Please enter WhatsApp Mobile No:</label>
                                  <div className="mobile-input-row">
                                    <PremiumSelect
                                      value={formData.whatsappCountryCode || '+91'}
                                      onChange={(val) => setFormData(prev => ({ ...prev, whatsappCountryCode: val }))}
                                      options={[
                                        { value: '+91', label: '+91 (India)' },
                                        { value: '+1', label: '+1 (USA)' },
                                        { value: '+44', label: '+44 (UK)' },
                                      ]}
                                      className="country-code-select"
                                    />
                                    <input
                                      type="text"
                                      name="whatsappNumber"
                                      className="form-control mt-0"
                                      value={formData.whatsappNumber}
                                      onChange={(e) => {
                                        const value = e.target.value.replace(/[^0-9]/g, '');
                                        setFormData(prev => ({ ...prev, whatsappNumber: value }));
                                      }}
                                      placeholder="Enter WhatsApp number"
                                      minLength={10}
                                      maxLength={16}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Marketing Communications */}
                        <div
                          className="highlight"
                          style={{
                            backgroundColor: formData.agreeForMarketing ? '#dcf1f6' : '#fafbfc',
                            borderLeftColor: formData.agreeForMarketing ? '#2c5966' : '#cbd5e1',
                            borderLeftStyle: 'solid',
                            borderLeftWidth: '4px'
                          }}
                        >
                          <div className="checkbox-wrapper-33">
                            <label className="checkbox">
                              <input
                                className="checkbox__trigger visuallyhidden"
                                type="checkbox"
                                name="confirmation2"
                                checked={formData.agreeForMarketing}
                                onChange={(e) => setFormData(prev => ({ ...prev, agreeForMarketing: e.target.checked }))}
                              />
                              <span className="checkbox__symbol">
                                <svg aria-hidden="true" className="icon-checkbox" width="28px" height="28px" viewBox="0 0 28 28" version="1" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M4 14l8 7L24 7"></path>
                                </svg>
                              </span>
                              <p className="checkbox__textwrapper" style={{ fontWeight: '600' }}>
                                Yes, I would like to receive marketing communications from Facilon Services Private Limited
                              </p>
                            </label>
                          </div>
                        </div>

                        {/* Terms and Conditions */}
                        <div
                          className="highlight"
                          style={{
                            backgroundColor: termsAccepted ? '#dcf1f6' : '#fafbfc',
                            borderLeftColor: termsAccepted ? '#2c5966' : '#cbd5e1',
                            borderLeftStyle: 'solid',
                            borderLeftWidth: '4px'
                          }}
                        >
                          <div className="checkbox-wrapper-33">
                            <label className="checkbox" onClick={(e) => !termsCheckboxEnabled && openTermsModal(e)}>
                              <input
                                className="checkbox__trigger visuallyhidden"
                                type="checkbox"
                                id="termsCheckbox"
                                name="agree_terms"
                                checked={termsAccepted}
                                disabled={!termsCheckboxEnabled}
                                onChange={(e) => termsCheckboxEnabled && setTermsAccepted(e.target.checked)}
                              />
                              <span className="checkbox__symbol">
                                <svg aria-hidden="true" className="icon-checkbox" width="28px" height="28px" viewBox="0 0 28 28" version="1" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M4 14l8 7L24 7"></path>
                                </svg>
                              </span>
                              <p className="checkbox__textwrapper" style={{ fontWeight: '600' }}>
                                I have read, understood and hereby accept the{' '}
                                <a href="javascript:void(0);" onClick={openTermsModal}>Terms and Conditions</a>
                                <span className="star-color">*</span>
                              </p>
                            </label>
                          </div>
                        </div>
                      </div>

                      {/* Submit Button */}
                      <div id="button_div" className="flex gap-2 justify-center single-field mb-0 mt-3 border-t pt-3">
                        <button
                          className="button-1"
                          type="submit"
                          id="submitBtn"
                          disabled={loading || !termsAccepted}
                        >
                          {loading ? 'Submitting...' : 'Submit'}
                        </button>
                        <button
                          type="button"
                          className="button-2"
                          onClick={() => navigate(-1)}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Panel: Creative Illustration Panel */}
            <div className="right-panel">
              <RegistrationVisualCard step="details" registerAs={1} />
            </div>
          </div>
        </div>
      </section>
      <CompactFooter />

      {/* Modals */}
      <TermsModal
        show={showTermsModal}
        onClose={closeTermsModal}
        onAccept={acceptTerms}
      />
    </>
  );
};

export default IntroducedInvestorStep4;
